/* FireBar Labs store locator — hub UI.
   Hydrates from <script id="fbl-locator-data">, drives Mapbox map + list +
   filters + sheet. Vanilla JS, no framework.

   Public globals expected on the page:
     window.FBL_LOCATOR_CONFIG = { mapboxToken, defaultCenter, defaultZoom,
                                   sectionId, pinColor, storeLinkPrefix }
*/
(function () {
  'use strict';

  const cfg = window.FBL_LOCATOR_CONFIG || {};
  let stores = [];

  // ---------- Visible diagnostic (persists if map fails) ----------
  // Diagnostic shows ONLY on errors. Success is silent.
  function diag(msg, ok) {
    const el = document.getElementById('fbl-locator-diag');
    const m = document.getElementById('fbl-locator-diag-msg');
    console.log('[fbl-locator]', msg);
    if (!el || !m) return;
    if (!ok) {
      el.hidden = false;
      el.dataset.ok = 'false';
      m.textContent = msg;
    }
  }
  function diagOK(msg) { console.log('[fbl-locator]', msg); }

  async function loadStores() {
    if (!cfg.storesJsonUrl) {
      diag('storesJsonUrl not configured in theme settings');
      return [];
    }
    // Retry up to 3 times with exponential backoff on network errors
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await fetch(cfg.storesJsonUrl, {cache: 'default'});
        if (!r.ok) throw new Error('HTTP ' + r.status);
        let data = await r.json();
        data = data.filter(s => s.published !== false && s.lat && s.lng);
        const scope = cfg.defaultScope || {};
        if (scope.city || scope.state) {
          data = data.filter(s =>
            (!scope.city || s.city === scope.city) &&
            (!scope.state || s.state === scope.state)
          );
        }
        return data;
      } catch (e) {
        console.warn('[fbl-locator] loadStores attempt ' + (attempt+1) + ' failed', e);
        if (attempt === 2) {
          diag('Could not load store data: ' + e.message);
          return [];
        }
        await new Promise(res => setTimeout(res, 500 * Math.pow(2, attempt)));
      }
    }
    return [];
  }

  // ---------- DOM refs ----------
  const $ = (sel) => document.querySelector(sel);
  const els = {
    section: document.querySelector('[data-section-id="' + cfg.sectionId + '"]'),
    list: $('#fbl-locator-list'),
    count: $('#fbl-locator-count'),
    empty: $('#fbl-locator-empty'),
    clear: $('#fbl-locator-clear'),
    search: $('#fbl-locator-search'),
    geo: $('#fbl-locator-geo'),
    sort: $('#fbl-locator-sort'),
    sheet: $('#fbl-locator-sheet'),
    sheetBody: $('#fbl-locator-sheet-body'),
    sheetClose: document.querySelector('.fbl-locator__sheet-close'),
    body: document.querySelector('.fbl-locator__body'),
    viewToggle: $('#fbl-locator-view-toggle'),
  };

  // ---------- State ----------
  const PAGE_SIZE = 10;
  const state = {
    filterType: 'all',
    openNowOnly: false,
    sortBy: 'distance',
    userLocation: null, // {lat, lng}
    selectedSlug: null,
    visibleCount: PAGE_SIZE, // for "View more" pagination on the list
  };

  // ---------- Utility: distance, hours, format ----------
  function haversine(lat1, lng1, lat2, lng2) {
    const R = 3958.8; // miles
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  function formatPhone(p) {
    if (!p) return '';
    const d = p.replace(/\D/g, '');
    if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return p;
  }

  function formatTime12(hhmm) {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    if (h === 24) return '12:00 AM';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2,'0')} ${ampm}`;
  }

  const DAY_KEYS = ['mon','tue','wed','thu','fri','sat','sun'];
  const DAY_LABELS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

  // Returns the day-of-week index (0=Mon..6=Sun) in a given IANA TZ.
  function dowInTZ(date, tz) {
    try {
      const wk = new Intl.DateTimeFormat('en-US', {timeZone: tz, weekday: 'short'}).format(date);
      const map = {Mon:0, Tue:1, Wed:2, Thu:3, Fri:4, Sat:5, Sun:6};
      return map[wk] ?? 0;
    } catch { return new Date().getDay() === 0 ? 6 : new Date().getDay() - 1; }
  }

  // Returns "HH:MM" minute-of-day string in a given TZ.
  function timeInTZ(date, tz) {
    try {
      const t = new Intl.DateTimeFormat('en-US', {timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit'}).format(date);
      return t.replace(/^24:/, '00:');
    } catch { return '00:00'; }
  }

  function isOpenNow(store) {
    const hours = store.hours || {};
    const tz = store.tz || 'America/New_York';
    const now = new Date();
    const dow = dowInTZ(now, tz);
    const today = hours[DAY_KEYS[dow]];
    if (!today) return false;
    const cur = timeInTZ(now, tz);
    // Compare as strings — works for HH:MM where close > open.
    if (today.close > today.open) {
      return cur >= today.open && cur < today.close;
    }
    // Crosses midnight (close <= open): open OR before close
    return cur >= today.open || cur < today.close;
  }

  function nextOpenSummary(store) {
    const hours = store.hours || {};
    const tz = store.tz || 'America/New_York';
    const now = new Date();
    const dow = dowInTZ(now, tz);
    const today = hours[DAY_KEYS[dow]];
    if (today && timeInTZ(now, tz) < today.open) {
      return `Opens ${formatTime12(today.open)}`;
    }
    if (today && isOpenNow(store)) {
      return `Open until ${formatTime12(today.close)}`;
    }
    // find next open day
    for (let i = 1; i <= 7; i++) {
      const d = (dow + i) % 7;
      const h = hours[DAY_KEYS[d]];
      if (h) return `Closed — opens ${DAY_LABELS[d]} ${formatTime12(h.open)}`;
    }
    return 'Hours unavailable';
  }

  function distanceLabel(miles) {
    if (miles < 0.1) return '<0.1 mi';
    if (miles < 10) return miles.toFixed(1) + ' mi';
    return Math.round(miles) + ' mi';
  }

  // ---------- Filtering / sorting ----------
  function filteredSorted() {
    let out = stores.slice();
    if (state.filterType !== 'all') {
      out = out.filter(s => s.type === state.filterType);
    }
    if (state.openNowOnly) {
      out = out.filter(s => isOpenNow(s));
    }
    if (state.userLocation) {
      out.forEach(s => { s._dist = haversine(state.userLocation.lat, state.userLocation.lng, s.lat, s.lng); });
    } else {
      out.forEach(s => { s._dist = null; });
    }
    if (state.sortBy === 'distance' && state.userLocation) {
      out.sort((a, b) => a._dist - b._dist);
    } else if (state.sortBy === 'state') {
      out.sort((a, b) => (a.state + a.city + a.name).localeCompare(b.state + b.city + b.name));
    } else {
      out.sort((a, b) => a.name.localeCompare(b.name));
    }
    return out;
  }

  // ---------- Render: list + map sync ----------
  function renderList(items) {
    els.list.innerHTML = '';
    els.empty.hidden = items.length > 0;
    els.count.textContent = items.length === stores.length
      ? `${items.length} retailers`
      : `${items.length} of ${stores.length} retailers`;
    // Paginate — only render up to visibleCount; show a "View more" button if there's more.
    const visibleItems = items.slice(0, state.visibleCount);
    const remaining = items.length - visibleItems.length;
    const frag = document.createDocumentFragment();
    visibleItems.forEach(s => {
      const open = isOpenNow(s);
      const li = document.createElement('li');
      li.className = 'fbl-locator__card';
      li.tabIndex = 0;
      li.dataset.slug = s.slug;
      const detailHref = (cfg.storeLinkPrefix || '/pages/store-') + s.slug;
      const photoHtml = s.photo
        ? `<a class="fbl-locator__card-photo-link" href="${detailHref}" aria-label="Open ${escapeHtml(s.name)} page"><img class="fbl-locator__card-photo" src="${escapeHtml(s.photo)}" alt="${escapeHtml(s.name)} storefront" loading="lazy" decoding="async" onerror="this.outerHTML='<div class=\\'fbl-locator__card-photo fbl-locator__card-photo--empty\\' aria-hidden=\\'true\\'></div>'"></a>`
        : `<div class="fbl-locator__card-photo fbl-locator__card-photo--empty" aria-hidden="true"></div>`;
      const ratingHtml = s.rating
        ? `<span class="fbl-locator__card-rating" aria-label="${s.rating} stars from ${s.reviews_count || 0} reviews">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17.27 18.18 21 16.54 13.97 22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" fill="#f59e0b"/></svg>
            ${parseFloat(s.rating).toFixed(1)}${s.reviews_count ? ` <span class="fbl-locator__card-rating-count">(${s.reviews_count})</span>` : ''}
          </span>`
        : '';
      li.innerHTML = `
        ${photoHtml}
        <div class="fbl-locator__card-body">
          <h3 class="fbl-locator__card-name"><a href="${detailHref}" class="fbl-locator__card-name-link">${escapeHtml(s.name)}</a></h3>
          <p class="fbl-locator__card-meta">${escapeHtml(s.city)}, ${escapeHtml(s.state)}</p>
          <div class="fbl-locator__card-foot">
            ${s._dist != null ? `<span class="fbl-locator__card-distance">${distanceLabel(s._dist)}</span>` : ''}
            ${ratingHtml}
            <span class="fbl-locator__card-status fbl-locator__card-status--${open ? 'open' : 'closed'}">${open ? 'Open' : 'Closed'}</span>
          </div>
        </div>
      `;
      // Card click opens the sheet (preview), but if the user clicks a link
      // inside the card the navigation wins (we let it bubble through).
      li.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        selectStore(s.slug, true);
      });
      li.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectStore(s.slug, true); }
      });
      frag.appendChild(li);
    });
    els.list.appendChild(frag);

    // "View more" footer — only when there are more items than visible
    if (remaining > 0) {
      const moreBtn = document.createElement('li');
      moreBtn.className = 'fbl-locator__more';
      const nextBatch = Math.min(remaining, PAGE_SIZE);
      moreBtn.innerHTML = `<button type="button" class="fbl-locator__btn fbl-locator__btn--secondary fbl-locator__more-btn">View ${nextBatch} more (${remaining} remaining)</button>`;
      moreBtn.querySelector('button').addEventListener('click', () => {
        state.visibleCount += PAGE_SIZE;
        rerender();
      });
      els.list.appendChild(moreBtn);
    }
  }

  function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  function renderStars(rating) {
    const full = Math.floor(rating);
    const half = (rating - full) >= 0.25 && (rating - full) < 0.75;
    const empty = 5 - full - (half ? 1 : 0);
    const star = (fill) => `<svg viewBox="0 0 24 24" width="14" height="14"><path d="M12 17.27 18.18 21 16.54 13.97 22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" fill="${fill}"/></svg>`;
    return star('#f59e0b').repeat(full) + (half ? `<svg viewBox="0 0 24 24" width="14" height="14"><defs><linearGradient id="hs"><stop offset="50%" stop-color="#f59e0b"/><stop offset="50%" stop-color="#e5e7eb"/></linearGradient></defs><path d="M12 17.27 18.18 21 16.54 13.97 22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" fill="url(#hs)"/></svg>` : '') + star('#e5e7eb').repeat(empty);
  }

  // ---------- Sheet (store detail) ----------
  function renderSheet(s) {
    const open = isOpenNow(s);
    const tz = s.tz || 'America/New_York';
    const todayDow = dowInTZ(new Date(), tz);
    const dirHref = s.place_id
      ? `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(s.place_id)}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.formatted_address || (s.street + ', ' + s.city + ', ' + s.state))}`;
    const detailHref = (cfg.storeLinkPrefix || '/pages/store-') + s.slug;

    const hoursRows = DAY_KEYS.map((k, i) => {
      const h = s.hours[k];
      const label = DAY_LABELS[i];
      const text = h ? `${formatTime12(h.open)} – ${formatTime12(h.close)}` : 'Closed';
      const isToday = i === todayDow ? ' is-today' : '';
      return `<tr class="${isToday.trim()}"><td>${label}</td><td>${text}</td></tr>`;
    }).join('');

    const photoStyle = s.photo ? `background-image:url('${s.photo}')` : '';
    const typeLabel = ({smoke_shop:'Smoke shop', head_shop:'Vape', dispensary:'Dispensary', wholesale:'Wholesale', convenience:'Convenience', other:'Retailer'}[s.type] || 'Retailer');

    const ratingBlock = s.rating
      ? `<div class="fbl-sheet-rating">
           <span class="fbl-sheet-stars" aria-hidden="true">${renderStars(parseFloat(s.rating))}</span>
           <strong>${parseFloat(s.rating).toFixed(1)}</strong>
           ${s.reviews_count ? `<span class="fbl-sheet-rating-count">${s.reviews_count} Google reviews</span>` : ''}
         </div>`
      : '';
    const photoAttr = '';
    const closedFlag = s.business_status === 'CLOSED_PERMANENTLY'
      ? `<div class="fbl-sheet-warning">⚠ This location is reported permanently closed by Google.</div>`
      : (s.business_status === 'CLOSED_TEMPORARILY'
          ? `<div class="fbl-sheet-warning">⚠ Temporarily closed per Google.</div>` : '');

    els.sheetBody.innerHTML = `
      ${s.photo ? `<div class="fbl-sheet-photo" style="${photoStyle}" role="img" aria-label="${escapeHtml(s.name)} storefront"></div>` : ''}
      ${photoAttr}
      ${closedFlag}
      <h2 class="fbl-sheet-name" id="fbl-locator-sheet-name">${escapeHtml(s.name)}</h2>
      <span class="fbl-sheet-type">${typeLabel}</span>
      ${ratingBlock}
      <p class="fbl-sheet-addr">${escapeHtml(s.formatted_address || (s.street + ', ' + s.city + ', ' + s.state + ' ' + s.zip))}</p>
      ${s._dist != null ? `<p class="fbl-sheet-distance">${distanceLabel(s._dist)} from your location</p>` : ''}
      <div class="fbl-sheet-actions">
        <a class="fbl-locator__btn fbl-locator__btn--primary" href="${dirHref}" target="_blank" rel="noopener">Get directions</a>
        ${s.phone ? `<a class="fbl-locator__btn fbl-locator__btn--secondary" href="tel:${s.phone}">${formatPhone(s.phone)}</a>` : ''}
        ${s.website ? `<a class="fbl-locator__btn fbl-locator__btn--secondary" href="${s.website}" target="_blank" rel="noopener">Visit website</a>` : ''}
        <a class="fbl-locator__btn fbl-locator__btn--ghost" href="${detailHref}">Store page →</a>
      </div>
      <div class="fbl-sheet-hours">
        <div class="fbl-sheet-hours-status fbl-sheet-hours-status--${open ? 'open' : 'closed'}">${nextOpenSummary(s)}</div>
        <table class="fbl-sheet-hours-table">${hoursRows}</table>
      </div>
    `;
    els.sheet.hidden = false;
    requestAnimationFrame(() => els.sheet.dataset.open = 'true');
  }

  function closeSheet() {
    els.sheet.dataset.open = 'false';
    setTimeout(() => { els.sheet.hidden = true; }, 250);
    state.selectedSlug = null;
    document.querySelectorAll('.fbl-locator__card.is-selected').forEach(el => el.classList.remove('is-selected'));
    document.querySelectorAll('.fbl-marker.is-selected').forEach(el => el.classList.remove('is-selected'));
  }

  function selectStore(slug, panToMap) {
    const s = stores.find(x => x.slug === slug);
    if (!s) return;
    state.selectedSlug = slug;
    document.querySelectorAll('.fbl-locator__card.is-selected').forEach(el => el.classList.remove('is-selected'));
    document.querySelectorAll('.fbl-marker.is-selected').forEach(el => el.classList.remove('is-selected'));
    const card = document.querySelector(`.fbl-locator__card[data-slug="${slug}"]`);
    if (card) {
      card.classList.add('is-selected');
      card.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }
    const markerEl = markerByslug.get(slug);
    if (markerEl) markerEl.classList.add('is-selected');
    if (panToMap && map) {
      map.flyTo({center: [s.lng, s.lat], zoom: Math.max(map.getZoom(), 13), essential: true});
    }
    renderSheet(s);
  }

  // ---------- Mapbox setup ----------
  let map = null;
  const markerByslug = new Map();

  function initMap() {
    const mapEl = document.getElementById('fbl-locator-map');
    if (!mapEl) {
      console.warn('[fbl-locator] map container missing');
      return;
    }
    if (!cfg.mapboxToken) {
      diag('Map unavailable: Mapbox token missing in section settings');
      mapEl.innerHTML = '<div style="padding:24px;color:#6b7280;text-align:center">Map unavailable — Mapbox token missing.</div>';
      return;
    }
    if (!window.mapboxgl) {
      diag('Map library (mapbox-gl-js) failed to load — check ad-blocker, CSP, or network');
      mapEl.innerHTML = '<div style="padding:24px;color:#6b7280;text-align:center">Map library failed to load. Check ad-blocker or refresh the page.</div>';
      return;
    }
    // (silent progress — only error/load events update the visible diag)
    try {
      mapboxgl.accessToken = cfg.mapboxToken;
      map = new mapboxgl.Map({
        container: 'fbl-locator-map',
        style: 'mapbox://styles/mapbox/light-v11',
        center: cfg.defaultCenter,
        zoom: cfg.defaultZoom,
        attributionControl: true,
        // Cooperative gestures: page scroll passes through unless user uses
        // Ctrl+scroll on desktop or two-finger pan on mobile. Critical so
        // mobile users can scroll past the map without zooming it.
        cooperativeGestures: true,
      });
      map.on('error', (e) => {
        const msg = e.error && e.error.message ? e.error.message : 'unknown';
        diag('Map error: ' + msg);
      });
      map.on('load', () => {
        diagOK('Map loaded ✓');
        // Force a resize on next frames in case the container was 0x0 at init
        // (common in flexbox/grid layouts inside iframes / preview environments)
        requestAnimationFrame(() => map.resize());
        setTimeout(() => map.resize(), 250);
        setTimeout(() => map.resize(), 1000);
      });
      // Also resize when the container's size actually changes
      if (window.ResizeObserver) {
        new ResizeObserver(() => map && map.resize()).observe(mapEl);
      }
    } catch (e) {
      console.error('[fbl-locator] map init failed', e);
      diag('Map init threw: ' + e.message);
      mapEl.innerHTML = '<div style="padding:24px;color:#6b7280;text-align:center">Map could not be loaded: ' + e.message + '</div>';
      return;
    }
    map.addControl(new mapboxgl.NavigationControl({showCompass: false}), 'top-right');
    map.on('load', () => {
      // Use Mapbox's built-in clustering on a GeoJSON source
      const features = stores.map(s => ({
        type: 'Feature',
        geometry: {type: 'Point', coordinates: [s.lng, s.lat]},
        properties: {slug: s.slug, name: s.name, city: s.city, state: s.state, type: s.type},
      }));
      map.addSource('stores', {
        type: 'geojson',
        data: {type: 'FeatureCollection', features},
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      });

      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'stores',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': cfg.pinColor || '#5da90a',
          'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 32],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#fff',
        },
      });
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'stores',
        filter: ['has', 'point_count'],
        layout: {'text-field': '{point_count_abbreviated}', 'text-size': 13, 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold']},
        paint: {'text-color': '#fff'},
      });
      map.addLayer({
        id: 'unclustered',
        type: 'circle',
        source: 'stores',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match', ['get', 'type'],
            'dispensary', '#16a34a',
            'head_shop', '#6366f1',
            'convenience', '#f59e0b',
            'wholesale', '#6b7280',
            cfg.pinColor || '#5da90a',
          ],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });

      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {layers: ['clusters']});
        const clusterId = features[0].properties.cluster_id;
        map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({center: features[0].geometry.coordinates, zoom});
        });
      });
      map.on('click', 'unclustered', (e) => {
        const slug = e.features[0].properties.slug;
        selectStore(slug, false);
      });
      map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'unclustered', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'unclustered', () => { map.getCanvas().style.cursor = ''; });
    });
  }

  function updateMapData() {
    if (!map || !map.getSource('stores')) return;
    const items = filteredSorted();
    const visible = new Set(items.map(s => s.slug));
    const features = stores
      .filter(s => visible.has(s.slug))
      .map(s => ({
        type: 'Feature',
        geometry: {type: 'Point', coordinates: [s.lng, s.lat]},
        properties: {slug: s.slug, name: s.name, city: s.city, state: s.state, type: s.type},
      }));
    map.getSource('stores').setData({type: 'FeatureCollection', features});
  }

  // ---------- Search via Mapbox geocoding ----------
  let searchTimer = null;
  function onSearch(q) {
    clearTimeout(searchTimer);
    if (!q || q.length < 3) return;
    searchTimer = setTimeout(() => {
      if (!cfg.mapboxToken) return;
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${cfg.mapboxToken}&country=us,pr,vi&limit=1&types=postcode,place,locality,address`;
      fetch(url).then(r => r.json()).then(data => {
        if (!data.features || !data.features.length) return;
        const [lng, lat] = data.features[0].center;
        state.userLocation = {lat, lng};
        if (map) map.flyTo({center: [lng, lat], zoom: 10, essential: true});
        rerender();
      }).catch(() => {});
    }, 400);
  }

  // ---------- Geolocation ----------
  function useGeolocation() {
    if (!navigator.geolocation) return;
    els.geo.disabled = true;
    navigator.geolocation.getCurrentPosition(
      pos => {
        state.userLocation = {lat: pos.coords.latitude, lng: pos.coords.longitude};
        if (map) map.flyTo({center: [pos.coords.longitude, pos.coords.latitude], zoom: 11, essential: true});
        rerender();
        els.geo.disabled = false;
      },
      err => { console.warn('geolocation', err); els.geo.disabled = false; },
      {timeout: 8000, maximumAge: 60000, enableHighAccuracy: false}
    );
  }

  // ---------- Wire up ----------
  function rerender() {
    renderList(filteredSorted());
    updateMapData();
  }

  document.querySelectorAll('.fbl-locator__chip:not(.fbl-locator__chip--toggle)').forEach(chip => {
    chip.addEventListener('click', () => {
      document.querySelectorAll('.fbl-locator__chip:not(.fbl-locator__chip--toggle)').forEach(c => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      state.filterType = chip.dataset.filter;
      state.visibleCount = PAGE_SIZE;  // reset pagination when filter changes
      rerender();
    });
  });
  document.querySelectorAll('.fbl-locator__chip--toggle').forEach(chip => {
    chip.addEventListener('click', () => {
      const wasActive = chip.classList.toggle('is-active');
      if (chip.dataset.toggle === 'open_now') state.openNowOnly = wasActive;
      state.visibleCount = PAGE_SIZE;
      rerender();
    });
  });
  els.sort?.addEventListener('change', e => { state.sortBy = e.target.value; state.visibleCount = PAGE_SIZE; rerender(); });
  els.search?.addEventListener('input', e => onSearch(e.target.value));
  els.search?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); onSearch(e.target.value); } });
  els.geo?.addEventListener('click', useGeolocation);
  els.sheetClose?.addEventListener('click', closeSheet);
  els.clear?.addEventListener('click', () => {
    state.filterType = 'all';
    state.openNowOnly = false;
    document.querySelectorAll('.fbl-locator__chip').forEach((c, i) => c.classList.toggle('is-active', i === 0));
    rerender();
  });
  els.viewToggle?.addEventListener('click', () => {
    const cur = els.body.dataset.view || 'map';
    const next = cur === 'list' ? 'map' : 'list';
    els.body.dataset.view = next;
    document.querySelectorAll('[data-view-label]').forEach(el => {
      el.hidden = el.dataset.viewLabel !== next;
    });
    if (next === 'map' && map) map.resize();
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeSheet(); });

  // Mobile default to map view; desktop split
  if (window.matchMedia('(max-width: 899px)').matches) {
    els.body.dataset.view = 'map';
  }

  // Wait for mapbox-gl-js + load store data (parallel) then init
  // Wrapped in top-level try/catch — if anything throws, the diag banner shows it
  async function safeStart() {
    try { await start(); }
    catch (e) {
      console.error('[fbl-locator] fatal init error', e);
      diag('Locator failed to initialize: ' + e.message);
    }
  }
  async function start() {
    const [data] = await Promise.all([
      loadStores(),
      new Promise(res => {
        const check = () => window.mapboxgl ? res() : setTimeout(check, 80);
        check();
      }),
    ]);
    stores = data;
    if (!stores.length) {
      els.count.textContent = 'No retailers available';
      return;
    }
    initMap();
    rerender();
    // Auto-fit map to filtered stores on city/state pages
    if (cfg.autoFitBounds && map && stores.length) {
      const bounds = stores.reduce((b, s) => b.extend([s.lng, s.lat]),
        new mapboxgl.LngLatBounds([stores[0].lng, stores[0].lat], [stores[0].lng, stores[0].lat]));
      // Wait for map style to be loaded, then fit
      const fit = () => map.fitBounds(bounds, {padding: 60, maxZoom: 14, duration: 0});
      if (map.loaded()) fit(); else map.on('load', fit);
    }
  }
  safeStart();
})();
